import { useEffect, useState, useContext, useCallback } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Check, Add, Remove } from '@mui/icons-material'
import {
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  TextareaAutosize,
  Tooltip,
  Alert,
  AlertTitle,
  CircularProgress,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import { Waypoints as HeaderIcon } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouteMatch, useHistory } from 'react-router-dom'
import { CvsAutoComplete, CvsButton, Paper } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { accountContext, integrationContext } from '@contexts'
import { api } from 'api'
import { showSnackbar } from 'functions'
import { MultiTargetUpload } from './multi-target-upload'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    padding: 24,
    paddingTop: 48,
    borderRadius: 0,
  },
  textField: {
    width: 200,
  },
}))

export const EditTarget = () => {
  const classes = useStyles()
  const history = useHistory()
  const match = useRouteMatch()
  const targetId = match.params.targetId === 'new' ? null : match.params.targetId
  const [loaded, setLoaded] = useState(false)
  const { fetchAccounts, byId: accountsById } = useContext(accountContext)
  const [realms, setRealms] = useState([])
  const [reports, setReports] = useState([])
  const dispatch = useDispatch()
  const [target, setTarget] = useState(null)
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [selectedRealm, setSelectedRealm] = useState(null)
  const [selectedReport, setSelectedReport] = useState(null)
  const [selectedType, setSelectedType] = useState(null)
  const [sendAdobeConfirmationEmail, setSendAdobeConfirmationEmail] = useState(false)
  const [confirmationEmail, setConfirmationEmail] = useState('')
  const [targetName, setTargetName] = useState('')
  const [targetDescription, setTargetDescription] = useState('')
  const [savingTarget, setSavingTarget] = useState(false)

  const { fetchIntegrations } = useContext(integrationContext)
  const [showGABanner, setShowGABanner] = useState(false)
  const [gaAccountId, setGaAccountId] = useState(null)
  const [gaPropertyId, setGaPropertyId] = useState(null)
  const [gaCustomDataSourceId, setGaCustomDataSourceId] = useState(null)
  const [gaCustomDataSourceSchema, setGaCustomDataSourceSchema] = useState(null)
  const [gaAccounts, setGaAccounts] = useState([])
  const [gaProperties, setGaProperties] = useState([])
  const [gaCustomDataSources, setGaCustomDataSources] = useState([])
  const [targetFields, setTargetFields] = useState({})
  const [multiTargets, setMultiTargets] = useState([])
  const [adobeFields, setAdobeFields] = useState([])
  const [adobeVersionOfTarget, setAdobeVersionOfTarget] = useState(null)
  const [mtusLoaded, setMtusLoaded] = useState(false)
  const [allAdobeFields, setAllAdobeFields] = useState({})
  const [connectors, setConnectors] = useState([])

  useEffect(() => {
    async function fetch() {
      const integrationData = await fetchIntegrations()
      const matchGAIntegration = integrationData.find(i => i.slug === 'google-analytics')
      setShowGABanner(matchGAIntegration)
    }
    fetch()
  }, [fetchIntegrations])

  const showInactiveConnectors = useSelector(
    state => state.Auth.features['show-inactive-connectors'],
  )

  const fetchConnectors = () => {
    api.get('/api/v1/connector-type').then(({ data }) => {
      const sorted = data.data.sort((a, b) => (a.name || '').localeCompare(b.name))
      setConnectors(showInactiveConnectors ? sorted : sorted.filter(con => con.active))
    })
  }

  const fetchAdobeFields = useCallback(
    (accountId, remoteRealmId, selRealm) => {
      api
        .get(`/api/v1/account/connector/${accountId}/realm/${remoteRealmId}/target/list`)
        .then(({ data }) => {
          if (remoteRealmId === selRealm) {
            setAdobeFields(data.targets)
            setAllAdobeFields({ ...allAdobeFields, [remoteRealmId]: data })
          } else {
            setAllAdobeFields({ ...allAdobeFields, [data.remoteRealmId]: data })
          }
        })
    },
    [selectedRealm, allAdobeFields],
  )

  const fetchTargetFields = tId => {
    api.get(`/api/v1/account/targets/${tId}/fields`).then(({ data }) => {
      setTargetFields(
        data.reduce(
          (memo, item) => ({
            ...memo,
            [item.name]: true,
          }),
          {},
        ),
      )
    })
  }

  const fetchMultiTargets = () => {
    setMtusLoaded(false)
    api.get(`/api/v1/account/targets/${targetId}/multi-targets`).then(({ data }) => {
      setMultiTargets(data.multiTargets)
      setMtusLoaded(true)
    })
  }

  useEffect(() => {
    // generate the string mtu options;
    if (adobeFields.length && selectedReport) {
      const adobeIndex = adobeFields.findIndex(af => af.id === selectedReport)
      const adobeBase = adobeFields[adobeIndex]
      setAdobeVersionOfTarget(adobeBase)
    }
  }, [adobeFields, selectedReport])

  const fetchRealms = account => {
    api.post(`/api/v1/account/connector/${account}/realm/`).then(res => {
      setRealms(res.data.realms)
    })
  }

  const fetchReports = (realm, account) => {
    api
      .get(`/api/v1/account/connector/${account || selectedAccount}/realm/${realm}/target/list`)
      .then(res => {
        setReports(res.data.targets)
      })
  }

  const fetchGAAccounts = account => {
    api.get(`/api/v1/account/connector/${account}/ga-account`).then(res => {
      setGaAccounts(res.data.gaAccounts)
    })
  }

  const fetchProperties = (gaAccount, account) => {
    api
      .get(
        `/api/v1/account/connector/${
          account || selectedAccount
        }/ga-account/${gaAccount}/property/list`,
      )
      .then(res => {
        setGaProperties(res.data.gaProperties)
      })
  }

  const fetchGaCustomDataSources = (property, account, gaAccount) => {
    api
      .get(
        `/api/v1/account/connector/${account || selectedAccount}/ga-account/${
          gaAccount || gaAccountId
        }/property/${property}/custom-data-source/list`,
      )
      .then(({ data }) => {
        setGaCustomDataSources(data.gaCustomDataSources)
      })
  }

  const saveTarget = () => {
    // new target
    setSavingTarget(true)
    const isAdobe = selectedType === 'adobe'
    return api
      .put('/api/v1/account/targets/', {
        id: targetId,
        name: targetName,
        nickname: targetDescription,
        connector_id: selectedAccount,
        remote_realm_id: isAdobe ? selectedRealm : null,
        ga_account_id: isAdobe ? null : gaAccountId,
        ga_property_id: isAdobe ? null : gaPropertyId,
        ga_custom_data_source_id: isAdobe ? null : gaCustomDataSourceId,
        ga_custom_data_source_schema: isAdobe ? null : gaCustomDataSourceSchema,
        aws_bucket_id: '',
        aws_bucket_folder_id: '',
        realm_name: isAdobe
          ? realms.find(r => r.remote_realm_id === selectedRealm).realm_name
          : null,
        remote_target_id: isAdobe ? selectedReport : null,
        connector: { connector_type_id: isAdobe ? 1 : 2 },
        connector_type_id: isAdobe ? 1 : 2,
        use_aa_confirmation_email:
          isAdobe && sendAdobeConfirmationEmail ? sendAdobeConfirmationEmail : '',
        aa_confirmation_email: isAdobe && confirmationEmail ? confirmationEmail : '',
      })
      .then(({ data }) => {
        setSavingTarget(false)
        dispatch(
          showSnackbar({
            message: 'Target saved successfully',
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
        if (!targetId) {
          history.replace(`/admin/targets/${data.target.id}`)
          setMtusLoaded(true)
        }
      })
      .catch(() => {
        dispatch(
          showSnackbar({
            message: t`Target failed to save. Please fill out all fields and try again.`,
            type: 'ERROR',
          }),
        )
      })
  }

  // const removeChip = chip => {
  //   setSelectedMTUs(selectedMTUs.filter(mtu => mtu !== chip))
  // }

  useEffect(() => {
    if (targetId) {
      api.get(`/api/v1/account/targets/${targetId}`).then(({ data }) => {
        setTarget(data)
        setLoaded(true)
        setTargetName(data.name)
        setTargetDescription(data.nickname)
        setSelectedAccount(data.connector_id)
        setSelectedType(data.connector.connector_type.slug === 'adobe' ? 'adobe' : 'google')
        if (data.connector.connector_type.slug === 'adobe') {
          // adobe specific sets
          setSelectedRealm(data.remote_realm_id)
          setSelectedReport(data.remote_target_id)
          setSendAdobeConfirmationEmail(data.use_aa_confirmation_email)
          setConfirmationEmail(data.aa_confirmation_email)
          fetchRealms(data.connector_id)
          fetchReports(data.remote_realm_id, data.connector_id)
          fetchAdobeFields(data.connector_id, data.remote_realm_id, data.remote_realm_id)
          fetchMultiTargets(targetId)
        } else {
          // google specific sets
          setGaAccountId(data.ga_account_id)
          setGaPropertyId(data.ga_property_id)
          setGaCustomDataSourceId(data.ga_custom_data_source_id)
          setGaCustomDataSourceSchema(data.ga_custom_data_source_schema)

          fetchGAAccounts(data.connector_id)
          fetchProperties(data.ga_account_id, data.connector_id)
          fetchGaCustomDataSources(data.ga_property_id, data.connector_id, data.ga_account_id)
        }
      })

      fetchTargetFields(targetId)
    } else {
      setTarget({})
      setLoaded(true)
    }
    fetchAccounts()
    fetchConnectors()
  }, [])

  // const handleChange = updated => {}

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ overflow: 'scroll' }}
    >
      <AdminHeader>
        <AdminHeader.PageIcon>
          <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.Title>{t`Edit target`}</AdminHeader.Title>
          <AdminHeader.Subtitle>
            {t`Use your connected accounts to create specific interactions within integrations`}
          </AdminHeader.Subtitle>
        </div>
      </AdminHeader>
      <Paper className={classes.paper}>
        {loaded ? (
          <div className={classes.table} style={{ height: 'calc(100% - 145px)' }}>
            <div>
              <Grid container spacing={2}>
                {selectedType && selectedType !== 'adobe' && showGABanner && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <AlertTitle>
                        {t`An improved version of Google Analytics targets is now available as an outbound integration!`}
                      </AlertTitle>
                      <Trans>
                        Please create new connections to Google Analytics via an outbound
                        integration and migrate any existing GA targets to outbound integrations
                        when you have a moment.
                      </Trans>
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <TextField
                    variant="outlined"
                    label={t`Target name`}
                    style={{ width: '100%' }}
                    required={true}
                    value={targetName}
                    onChange={e => {
                      setTargetName(e.target.value)
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    variant="outlined"
                    style={{ width: '100%' }}
                    label={t`Description`}
                    value={targetDescription}
                    onChange={e => {
                      setTargetDescription(e.target.value)
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CvsAutoComplete
                    label={t`Integration type`}
                    style={{ margin: 0 }}
                    required={true}
                    options={Object.values(connectors)
                      .filter(t => t.is_analytic === 1)
                      .map(t => {
                        return {
                          value: t.slug,
                          label: t.name,
                        }
                      })}
                    onSelected={item => {
                      setSelectedType(item)
                    }}
                    selected={selectedType}
                  />
                </Grid>
                {selectedType && selectedType === 'adobe' && (
                  <>
                    <Grid item xs={6}>
                      <CvsAutoComplete
                        label={t`Adobe account`}
                        style={{ margin: 0 }}
                        required={true}
                        options={Object.values(accountsById)
                          .filter(t => t.connector_type_id === 1)
                          .map(t => {
                            return {
                              value: t.id,
                              label: t.name,
                            }
                          })}
                        onSelected={item => {
                          setSelectedAccount(item)
                          fetchRealms(item)
                        }}
                        selected={selectedAccount}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CvsAutoComplete
                        label={t`Report suite`}
                        style={{ margin: 0 }}
                        required={true}
                        options={realms.map(t => {
                          return {
                            value: t.remote_realm_id,
                            label: t.realm_name,
                          }
                        })}
                        onSelected={item => {
                          setSelectedRealm(item)
                          fetchReports(item)
                          fetchAdobeFields(selectedAccount, item, item)
                        }}
                        selected={selectedRealm}
                        disabled={!selectedAccount}
                        showSpinner={selectedAccount && !realms.length}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CvsAutoComplete
                        label={t`Report`}
                        required={true}
                        style={{ margin: 0 }}
                        options={reports.map(t => {
                          return {
                            value: t.id,
                            label: t.name,
                          }
                        })}
                        onSelected={item => {
                          setSelectedReport(item)
                        }}
                        selected={selectedReport}
                        disabled={!selectedRealm}
                        showSpinner={selectedRealm && !reports.length}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        style={{ margin: 0, height: 70 }}
                        control={
                          <Checkbox
                            checked={sendAdobeConfirmationEmail}
                            onChange={(e, newVal) => {
                              setSendAdobeConfirmationEmail(newVal)
                            }}
                            value={sendAdobeConfirmationEmail}
                            style={{ padding: 2 }}
                            color="secondary"
                          />
                        }
                        label={t`Send Adobe Analytics import confirmation emails`}
                      />
                    </Grid>
                    {sendAdobeConfirmationEmail && (
                      <Grid item xs={6}>
                        <TextField
                          variant="outlined"
                          label={t`Confirmation email`}
                          style={{ width: '100%' }}
                          value={confirmationEmail}
                          onChange={e => {
                            setConfirmationEmail(e.target.value)
                          }}
                        />
                      </Grid>
                    )}
                  </>
                )}
                {selectedType && selectedType === 'ga' && (
                  <>
                    <Grid item xs={6}>
                      <CvsAutoComplete
                        label={t`GA account`}
                        required={true}
                        style={{ margin: 0 }}
                        options={Object.values(accountsById)
                          .filter(t => t.connector_type_id === 2)
                          .map(t => {
                            return {
                              value: t.id,
                              label: t.name,
                            }
                          })}
                        onSelected={item => {
                          setSelectedAccount(item)
                          fetchGAAccounts(item)
                        }}
                        selected={selectedAccount}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CvsAutoComplete
                        label={t`Google Analytics account`}
                        style={{ margin: 0 }}
                        required={true}
                        options={gaAccounts.map(t => {
                          return {
                            value: t.ga_account_id,
                            label: t.ga_account_name,
                          }
                        })}
                        onSelected={item => {
                          setGaAccountId(item)
                          fetchProperties(item)
                        }}
                        selected={gaAccountId}
                        disabled={!selectedAccount}
                        showSpinner={selectedAccount && !gaAccounts.length}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CvsAutoComplete
                        label={t`GA property`}
                        style={{ margin: 0 }}
                        required={true}
                        options={gaProperties.map(t => {
                          return {
                            value: t.ga_property_id,
                            label: t.ga_property_name,
                          }
                        })}
                        onSelected={item => {
                          setGaPropertyId(item)
                          fetchGaCustomDataSources(item)
                        }}
                        selected={gaPropertyId}
                        disabled={!gaAccountId}
                        showSpinner={gaAccountId && !gaProperties.length}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CvsAutoComplete
                        label={t`Custom data source`}
                        style={{ margin: 0 }}
                        options={gaCustomDataSources.map(t => {
                          return {
                            value: t.ga_custom_data_source_id,
                            label: t.ga_custom_data_source_name,
                          }
                        })}
                        onSelected={item => {
                          setGaCustomDataSourceId(item)
                        }}
                        selected={gaCustomDataSourceId}
                        disabled={!gaPropertyId}
                        required={true}
                        showSpinner={gaPropertyId && !gaCustomDataSources.length}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextareaAutosize
                        minRows={4}
                        disabled={!gaCustomDataSourceId}
                        style={{ width: '100%', minHeight: 100 }}
                        placeholder={t`GA custom data source schema`}
                        value={gaCustomDataSourceSchema}
                        onChange={e => {
                          setGaCustomDataSourceSchema(e.target.value)
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </div>
            <div style={{ textAlign: 'right', paddingTop: 24 }}>
              <CvsButton
                // variant="contained"
                color="secondary"
                onClick={saveTarget}
                size="large"
                disabled={
                  !targetName ||
                  !selectedAccount ||
                  !(gaCustomDataSourceId || selectedReport) ||
                  savingTarget
                }
              >
                {savingTarget ? t`Saving` : t`Save`}
              </CvsButton>
            </div>
          </div>
        ) : (
          <div style={{ padding: 24, display: 'flex', width: '100%', justifyContent: 'center' }}>
            <CircularProgress color="primary" />
          </div>
        )}
      </Paper>
      {selectedReport && selectedType === 'adobe' && adobeVersionOfTarget && (
        <Paper style={{ padding: 24, borderRadius: 0 }}>
          <Typography variant="h6" gutterBottom>
            {t`Target fields`}
          </Typography>
          {adobeVersionOfTarget.targetFields.map(tf => (
            <div
              key={tf.id ?? tf.name}
              style={{ paddingBottom: 4, display: 'flex', alignItems: 'center' }}
            >
              {targetFields[tf.name] && (
                <Tooltip title={t`This field exists in Adobe and Claravine`} placement="left-end">
                  <Check style={{ paddingRight: 4 }} />
                </Tooltip>
              )}
              {!targetFields[tf.name] && (
                <Tooltip
                  title={t`This field exists in Adobe and not in Claravine. Please save this target.`}
                  placement="left-end"
                >
                  <Add style={{ paddingRight: 4 }} />
                </Tooltip>
              )}
              <span>{tf.name}</span>
            </div>
          ))}
          {Object.keys(targetFields).map(
            tf =>
              !adobeVersionOfTarget?.classifications_names.includes(tf) && (
                <div key={tf} style={{ paddingBottom: 4, display: 'flex', alignItems: 'center' }}>
                  <Tooltip
                    title={t`This field doesn't exist in Adobe, but does in Claravine. Please save this target.`}
                    placement="left-end"
                  >
                    <Remove style={{ paddingRight: 4 }} />
                  </Tooltip>
                  {tf}
                </div>
              ),
          )}
          {targetId && (
            <Typography variant="caption">
              {t`If there are any new fields available or fields that need to be removed, you'll need to save this target before it will work correctly in submissions.`}
            </Typography>
          )}
        </Paper>
      )}
      {targetId && selectedType === 'adobe' && (
        <Paper style={{ borderRadius: 0 }}>
          <div style={{ padding: 24 }}>
            <Typography variant="caption">
              {t`Multi Targets will send a copy of submissions to all multi-targets. Multi Targets must contain all of the exact classifications as the base target saved above. Only matching options in the selected account above are available to select.`}
            </Typography>
          </div>
          {mtusLoaded ? (
            <MultiTargetUpload
              multiTargets={multiTargets}
              adobeFields={allAdobeFields}
              fetchAdobeFields={fetchAdobeFields}
              accountId={selectedAccount}
              target={target}
              realms={realms}
            />
          ) : (
            <div style={{ padding: 24, display: 'flex', width: '100%', justifyContent: 'center' }}>
              <CircularProgress color="primary" />
            </div>
          )}
        </Paper>
      )}
    </motion.div>
  )
}
